import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Megaphone, CaretRight, Users, LockKey, CurrencyInr, Share } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import usePullToRefresh from "@/lib/usePullToRefresh";
import PullToRefreshIndicator from "@/components/PullToRefreshIndicator";

const fmtCur = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const CAMPAIGN_TYPES = ["Budget Based", "Creator Based"];
const STATUSES = ["Active", "On Hold", "Completed"];

export default function CampaignsList() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ brand_name: "", campaign_name: "", no_of_creators: 0, objective: "", commercial: 0, additional_costs: 0, additional_costs_note: "", status: "Active", campaign_type: "Budget Based", start_date: "", end_date: "", notes: "", assigned_to: [] });
  const [saving, setSaving] = useState(false);
  const [executives, setExecutives] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/campaigns", { params });
      setItems(data);
    } finally { setLoading(false); }
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const ptr = usePullToRefresh(load);

  useEffect(() => {
    if (isAdmin && dialogOpen) {
      api.get("/users", { params: { status: "approved" } })
        .then(({ data }) => setExecutives(data.filter((u) => u.role === "executive")))
        .catch(() => setExecutives([]));
    }
  }, [isAdmin, dialogOpen]);

  const toggleAssignee = (uid) => {
    setForm((f) => {
      const has = (f.assigned_to || []).includes(uid);
      return { ...f, assigned_to: has ? f.assigned_to.filter((x) => x !== uid) : [...(f.assigned_to || []), uid] };
    });
  };

  const create = async () => {
    if (!form.brand_name.trim()) { toast.error("Brand name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/campaigns", {
        ...form,
        commercial: parseFloat(form.commercial || 0) || 0,
      });
      toast.success("Campaign created");
      setDialogOpen(false);
      setForm({ brand_name: "", campaign_name: "", no_of_creators: 0, objective: "", commercial: 0, status: "Active", campaign_type: "Budget Based", start_date: "", end_date: "", notes: "", assigned_to: [] });
      navigate(`/campaigns/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create");
    } finally { setSaving(false); }
  };

  const statusColor = (s) => ({
    Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    "On Hold": "bg-amber-50 text-amber-700 border-amber-200",
    Completed: "bg-slate-100 text-slate-700 border-slate-200",
  }[s] || "bg-slate-100 text-slate-700 border-slate-200");

  return (
    <div className="space-y-5" data-testid="campaigns-list-page">
      <PullToRefreshIndicator {...ptr} />
      <div className="flex flex-wrap items-start sm:items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Workspace</div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">Campaigns</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">One brand campaign per row. Open one to manage its creators.</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white shrink-0"
          data-testid="new-campaign-btn"
        >
          <Plus size={16} className="mr-1.5" /> <span className="hidden xs:inline">New </span>campaign
        </Button>
      </div>

      <Card className="p-3 flex flex-wrap gap-2 items-center border border-slate-200 shadow-none">
        <Input
          placeholder="Search brand or objective…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          className="flex-1 min-w-[180px]"
          data-testid="search-campaigns-input"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-campaign-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border border-dashed border-slate-300 shadow-none">
          <Megaphone size={40} className="mx-auto text-slate-300 mb-3" weight="duotone" />
          <div className="font-heading text-lg font-semibold text-slate-900">No campaigns yet</div>
          <p className="text-sm text-slate-500 mt-1">Create your first brand campaign to start adding creators.</p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="empty-new-campaign-btn"
          >
            <Plus size={16} className="mr-1.5" /> New campaign
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => (
            <button
              key={c.id}
              onClick={(e) => { e.preventDefault(); window.open(`/campaigns/${c.id}`, "_blank", "noopener"); }}
              onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); window.open(`/campaigns/${c.id}`, "_blank", "noopener"); } }}
              className="text-left"
              data-testid={`campaign-card-${c.id}`}
            >
              <Card className="p-5 border border-slate-200 shadow-none hover:shadow-md hover:-translate-y-[1px] transition-all">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-heading text-lg font-semibold text-slate-900 truncate">{c.brand_name}</div>
                    {c.campaign_name && (
                      <div className="text-xs font-medium text-blue-600 mt-0.5 truncate">{c.campaign_name}</div>
                    )}
                    <div className="text-xs text-slate-500 mt-1 line-clamp-2">{c.objective || "No objective set"}</div>
                  </div>
                  <Badge variant="outline" className={`${statusColor(c.status)} text-[10px] rounded-full whitespace-nowrap`}>
                    {c.status || "Active"}
                  </Badge>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Users size={11} /> Creators</div>
                    <div className="stat-num text-lg font-semibold text-slate-900 mt-0.5">
                      {c.live_count || 0}{c.no_of_creators ? <span className="text-xs text-slate-400 font-normal"> / {c.no_of_creators}</span> : null}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><LockKey size={11} /> Locked</div>
                    <div className="stat-num text-lg font-semibold text-slate-900 mt-0.5">{c.locked_count}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Share size={11} /> Shared</div>
                    <div className="stat-num text-lg font-semibold text-slate-900 mt-0.5">{c.shared_count || 0}</div>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                  {(() => {
                    const isCB = c.campaign_type === "Creator Based";
                    const client = Number(c.client_cost_total || 0);
                    const internal = Number(c.internal_cost_total || 0);
                    const paid = Number(c.scoped_paid || 0);
                    const addCostsTotal = Number(c.additional_costs_total || 0);
                    const addCostsPaid = Number(c.additional_costs_paid || 0);
                    const feePct = Number(c.agency_fee_pct || 0);
                    const feeAmt = isCB ? client * (feePct / 100) : 0;
                    const budgetVal = isCB ? client + feeAmt : Number(c.commercial || 0);
                    const allocVal = isCB ? internal + addCostsTotal : Number(c.allocated_spend || 0);
                    const pendVal = isCB ? Math.max((internal + addCostsTotal) - (paid + addCostsPaid), 0) : Number(c.pending_payout || 0);
                    return (
                      <>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500">Budget</div>
                          <div className="stat-num text-sm font-semibold text-slate-900 mt-0.5" data-testid={`card-budget-${c.id}`}>{fmtCur(budgetVal)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500">Allocated</div>
                          <div className="stat-num text-sm font-semibold text-slate-900 mt-0.5" data-testid={`card-allocated-${c.id}`}>{fmtCur(allocVal)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500">Pending</div>
                          <div className="stat-num text-sm font-semibold text-rose-600 mt-0.5" data-testid={`card-pending-${c.id}`}>{fmtCur(pendVal)}</div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-2 text-xs">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Assigned to</div>
                    {(c.assigned_users || []).length === 0 ? (
                      <div className="text-slate-400 italic">Unassigned</div>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {c.assigned_users.slice(0, 3).map((u) => (
                          <span key={u.id} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full px-2 py-0.5 text-[11px]" title={u.email}>
                            <span className="w-4 h-4 rounded-full bg-blue-600 text-white flex items-center justify-center text-[9px] font-semibold">
                              {(u.name || "?").charAt(0).toUpperCase()}
                            </span>
                            {u.name}
                          </span>
                        ))}
                        {c.assigned_users.length > 3 && (
                          <span className="inline-flex items-center bg-slate-100 text-slate-600 rounded-full px-2 py-0.5 text-[11px]">
                            +{c.assigned_users.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <CaretRight size={14} className="text-slate-400 shrink-0" />
                </div>
              </Card>
            </button>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="new-campaign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">New campaign</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Brand name *</Label>
              <Input
                value={form.brand_name}
                onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
                placeholder="e.g. Boat, Mamaearth"
                className="mt-1"
                data-testid="field-brand-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Campaign name</Label>
                <Input
                  value={form.campaign_name}
                  onChange={(e) => setForm({ ...form, campaign_name: e.target.value })}
                  placeholder="e.g. Rockerz Launch 2026"
                  className="mt-1"
                  data-testid="field-campaign-name"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">No. of creators</Label>
                <Input
                  type="number"
                  value={form.no_of_creators}
                  onChange={(e) => setForm({ ...form, no_of_creators: e.target.value })}
                  placeholder="Planned count"
                  className="mt-1"
                  data-testid="field-no-of-creators"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Objective</Label>
              <Textarea
                value={form.objective}
                onChange={(e) => setForm({ ...form, objective: e.target.value })}
                placeholder="What's the brand trying to achieve?"
                rows={2}
                className="mt-1"
                data-testid="field-objective"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Commercial / budget (₹)</Label>
                <Input
                  type="number"
                  value={form.commercial}
                  onChange={(e) => setForm({ ...form, commercial: e.target.value })}
                  className="mt-1"
                  data-testid="field-commercial"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1" data-testid="field-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Campaign type</Label>
              <Select value={form.campaign_type} onValueChange={(v) => setForm({ ...form, campaign_type: v })}>
                <SelectTrigger className="mt-1" data-testid="field-campaign-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CAMPAIGN_TYPES.map((s) => <SelectItem key={s} value={s} data-testid={`campaign-type-option-${s.replace(/\s+/g, "-").toLowerCase()}`}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 mt-1">
                {form.campaign_type === "Creator Based"
                  ? "Track per-creator internal cost + client cost, and auto-compute margin."
                  : "Track a single commercial budget across the campaign."}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Start date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" data-testid="field-start-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">End date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" data-testid="field-end-date" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="mt-1"
                data-testid="field-notes"
              />
            </div>

            {isAdmin && (
              <div className="pt-2 border-t border-slate-200">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Assign executives</Label>
                <div className="mt-2 max-h-40 overflow-y-auto space-y-1.5 border border-slate-200 rounded-md p-2 bg-slate-50/50">
                  {executives.length === 0 ? (
                    <div className="text-xs text-slate-500 p-2">No approved team members yet.</div>
                  ) : executives.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-white text-sm">
                      <Checkbox
                        checked={(form.assigned_to || []).includes(u.id)}
                        onCheckedChange={() => toggleAssignee(u.id)}
                        data-testid={`assign-new-${u.id}`}
                      />
                      <span className="flex-1">
                        <span className="text-slate-900">{u.name}</span>
                        <span className="text-slate-500 text-xs ml-2">{u.email}</span>
                        {u.role === "admin" && <span className="text-[10px] uppercase tracking-wider text-blue-600 ml-2">admin</span>}
                      </span>
                    </label>
                  ))}
                </div>
                <div className="text-[11px] text-slate-500 mt-1.5">Anyone assigned — admin or executive — will see this campaign.</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={create}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="create-campaign-btn"
            >
              {saving ? "Creating…" : "Create campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
